import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { api } from "../../../../api/api";
import { toast } from "react-toastify";
import RulesTable from "../RulesTable/RulesTable.component";
import { LoaderSpinner2 } from "../../../../components";
import styled from "styled-components";
import { SET_PL_TABLES } from "store/reducers/products/tables/tables.actions";
import { useDispatch } from "react-redux";
import { useTypedSelector } from "store";

interface EdgeDetailsPopupProps {
  showEdgePopup: boolean;
  setShowEdgePopup: (show: boolean) => void;
  tablesId: string[];
  productLineId: string;
}
/**
 * A popup component that displays details about rule tables associated with an edge
 * in the property relationship graph.
 *
 * @param showEdgePopup - Boolean flag to control visibility of the popup
 * @param setShowEdgePopup - Function to update the visibility state
 * @param tablesId - Array of table IDs to display
 * @param productLineId - ID of the current product line
 */
export const EdgeDetailsPopup: React.FC<EdgeDetailsPopupProps> = ({
  showEdgePopup,
  setShowEdgePopup,
  tablesId,
  productLineId,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const dispatch = useDispatch();
  const { byId: rulesTablesById } = useTypedSelector(
    (state) => state.products.tables
  );

  useEffect(() => {
    if (showEdgePopup && tablesId.length > 0) {
      setIsLoading(true);
      Promise.all(tablesId.map((id) => api.getRuleTable(productLineId, id)))
        .then((tables) => {
          const newAllIds: Array<any> = [];
          const newById = {};

          if (!tables) return;
          tables.map((rulesTable) => {
            newAllIds.push(rulesTable.data.id);
            newById[rulesTable.data.id] = rulesTable.data;
          });

          dispatch({
            type: SET_PL_TABLES,
            payload: {
              byId: newById,
              allIds: newAllIds,
              page: 0,
              total: tables.length,
            },
          });
        })
        .catch((error) => {
          console.error("Error fetching rule tables:", error);
          toast.error("Failed to load rule details.");
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [showEdgePopup, tablesId, productLineId]);

  return (
    <Dialog
      open={showEdgePopup}
      onClose={() => setShowEdgePopup(false)}
      maxWidth="xl"
      fullWidth
    >
      <DialogTitle>
        Table
        <IconButton
          onClick={() => setShowEdgePopup(false)}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {isLoading ? (
          <LoadingStyledWrapper>
            <Typography>Fetching Rules Tables </Typography>
            <LoaderSpinner2 />
          </LoadingStyledWrapper>
        ) : (
          rulesTablesById &&
          Object.keys(rulesTablesById).length > 0 &&
          Object.keys(rulesTablesById).map((id) => (
            <RuletableStyledWrapper key={id}>
              <RulesTable id={id} ruleTableData={rulesTablesById[id]} />
            </RuletableStyledWrapper>
          ))
        )}
      </DialogContent>
    </Dialog>
  );
};

const RuletableStyledWrapper = styled.div`
  border-bottom: 1px solid ${(props) => props.theme.colors.tables.borders};
  padding-top: 1px;
  padding-bottom: 32px;
  margin-bottom: 32px;
  margin-left: 16px;
  margin-right: 16px;
`;

const LoadingStyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: "20px";
`;
